import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { getTestCasesBySuite } from '../../../../modules/testsuites';
import TableComponent from '../../../elements/TableComponent';
import { testCasestableHeader } from '../../test-case/testCaseTableHead';
import { Col, Form, Row } from 'react-bootstrap';
import MultiSelectDropdown from '../../../elements/MultiSelectDropdown';

const SelectTestCasesFromSuiteModal = ({
  suite,
  selectedTestCases,
  setSelectedTestCases,
  handleBack,
  testCycleId,
  moduleOptions = [],
  technologyLayerOptions = [],
  labelsOptions = [],
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('desc');
  const [filters, setFilters] = useState({
    module: 'All',
    technologyLayer: 'All',
    labels: [],
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchTestCases = async () => {
      setLoading(true);
      try {
        const response = await getTestCasesBySuite(suite.id, page, perPage, true, testCycleId);
        const enrichedTestCases = (response.testcases || []).map((testCase) => ({
          ...testCase,
          labelNames:
            testCase.labels
              ?.map((id) => labelsOptions.find((label) => label.id === id)?.name)
              .filter(Boolean) || [],
        }));
        setData(enrichedTestCases);
        setTotalCount(response.total);
      } catch (error) {
        console.error('Failed to fetch test cases', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestCases();
  }, [suite, testCycleId, page, perPage]);

  const handleFilters = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedLabelNames = useMemo(
    () =>
      filters.labels
        .map((id) => labelsOptions.find((opt) => opt.id === id)?.name)
        .filter(Boolean),
    [filters.labels, labelsOptions]
  );

  const filteredData = useMemo(() => {
    return data.filter((tc) => {
      const matchesModule =
        filters.module === 'All' || tc.module?.id === filters.module;
      const matchesTechLayer =
        filters.technologyLayer === 'All' ||
        tc?.technology_layer === filters.technologyLayer;
      const matchesLabels =
        selectedLabelNames.length === 0 ||
        tc.labelNames?.some((name) => selectedLabelNames.includes(name));

      return matchesModule && matchesTechLayer && matchesLabels;
    });
  }, [data, filters, selectedLabelNames]);

  const filteredModuleOptions = useMemo(() => {
    const usedModuleIds = new Set(data.map((tc) => tc.module?.id).filter(Boolean));
    return moduleOptions.filter((mod) => usedModuleIds.has(mod.id));
  }, [data, moduleOptions]);

  const filteredLabelOptions = useMemo(() => {
    const usedLabelIds = new Set(data.flatMap((tc) => tc.labels || []));
    return labelsOptions.filter((label) => usedLabelIds.has(label.id));
  }, [data, labelsOptions]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      labels: prev.labels.filter((id) =>
        filteredLabelOptions.some((opt) => opt.id === id)
      ),
    }));
  }, [filteredLabelOptions]);
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <div className="mt-2">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">
          Test Suite: <strong>{suite.name}</strong>
        </Typography>

        <Button
          title="Back to Test Suites"
          onClick={handleBack}
          variant="outlined"
          startIcon={<ArrowBack />}>
          Back to Test Suites
        </Button>
      </Box>
      <Row>
        <Col md={9} className="d-flex gap-3 justify-content-end">
          <Form.Group controlId="technologyLayer" className="mb-0 w-50">
            <Form.Label>Technology Layer</Form.Label>
            <Form.Select
              name="technologyLayer"
              value={filters.technologyLayer}
              onChange={handleFilters}>
              <option value="All">All</option>
              {technologyLayerOptions.map(({ id, name }) => (
                <option key={id} value={name}>
                  {name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="module" className="mb-0 w-50">
            <Form.Label>Module</Form.Label>
            <Form.Select
              name="module"
              value={filters.module}
              onChange={handleFilters}>
              <option value="All">All</option>
              {filteredModuleOptions.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="labels" className="mb-0 w-50">
            <Form.Label>Labels</Form.Label>
            <MultiSelectDropdown
              options={filteredLabelOptions}
              selectedValues={filters.labels}
              onChange={(selected) =>
                setFilters((prev) => ({
                  ...prev,
                  labels: selected,
                }))
              }
              maxSelections={labelsOptions.length}
              placeholder="Select Labels"
              name='labels'
            />
          </Form.Group>
        </Col>
      </Row>
      <TableComponent
        data={filteredData}
        headerTabs={testCasestableHeader}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        isLoading={loading}
        striped
        showCheckbox
        selected={selectedTestCases}
        setSelected={setSelectedTestCases}
        isRowDisabled={(row) => row.is_linked_to_cycle}
        tablekey="test cases"
        page={page}
        perPage={perPage}
        totalCount={totalCount}
        showPages={true}
        showCount={true}
        handlePageChange={handlePageChange}
        setPerPage={handlePageSizeChange}
        showRowCount={true}
      />
    </div>
  );
};

export default SelectTestCasesFromSuiteModal;
